/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 9/23/2024
 * for userx-support
 */

import axios from 'axios';
import supportAPI from "./supportAPI";

const AI_API_KEY = process.env.REACT_APP_AI_API_KEY; // Store your key securely in .env

const openChatAPI =  axios.create({
    baseURL: 'https://api.openai.com/v1/chat/completions', // OpenAI completion endpoint
    headers: {
        'Authorization': `Bearer ${AI_API_KEY}`,
        'Content-Type': 'application/json',
    }
});

// javascript
/**
 * Send a chat prompt to the OpenAI chat completions endpoint with retry-on-429.
 * @param {string} prompt - User prompt to send.
 * @param {object} [opts] - Optional settings: model, temperature, max_tokens, system (system prompt).
 * @param {number} [retries=3] - Retry attempts on 429 responses.
 * @returns {Promise<{ text: string|null, raw: any }>} - Assistant text and full response.
 */
export const sendPrompt = async (prompt, opts = {}, retries = 3) => {
    const {
              model = 'gpt-4o',
              temperature = 0.7,
              max_tokens = 1000,
              system = null,
          } = opts;

    try {
        const messages = [];
        if (system) messages.push({ role: 'system', content: system });
        messages.push({ role: 'user', content: prompt });

        const payload = {
            model,
            messages,
            temperature,
            max_tokens,
        };

        // POST to baseURL (which is already set to the chat completions endpoint)
        const response = await openChatAPI.post('/', payload);

        const assistantText = response?.data?.choices?.[0]?.message?.content ?? null;
        return { text: assistantText, raw: response.data };
    } catch (error) {
        // Retry on rate limit
        const status = error?.response?.status;
        if (status === 429 && retries > 0) {
            const retryAfterHeader = error.response.headers['retry-after'];
            const waitSeconds = parseInt(retryAfterHeader, 10) || 2;
            console.warn(`Rate limited. Retrying in ${waitSeconds} seconds...`);
            await new Promise((resolve) => setTimeout(resolve, waitSeconds * 1000));
            return sendPrompt(prompt, opts, retries - 1);
        }

        // Bubble up other errors
        console.error('sendPrompt error:', error?.message ?? error);
        throw error;
    }
};


/**
 * Function to generate task list with retry logic for handling 429 Too Many Requests
 * @param {string} title - Ticket title
 * @param {string} description - Ticket description
 * @param {number} retries - Number of retry attempts (default 3)
 * @returns {Promise<Object>} - A JSON object representing the generated task list
 */
export const generateTaskList = async (title, description, retries = 3) => {
    try {

        const response = await supportAPI.post(`/openai/generate-invoice-summary`, {
            title: title,
            description: description
        });

        const taskList = response.data

        // Try parsing the task list directly as a JSON array of instructions
        const parsedTaskList = JSON.parse(taskList);

        // Ensure the parsed response is an array and each element is a string (instruction)
        if (Array.isArray(parsedTaskList) && parsedTaskList.every(task => typeof task === 'string')) {
            return parsedTaskList; // Return the array of task instructions
        } else {
            throw new Error('Invalid response format. Expected an array of task strings.');
        }

    } catch (error) {
        if (error.response && error.response.status === 429) {
            const retryAfter = error.response.headers['retry-after'] || 2; // Default wait of 2 seconds if not specified
            console.warn(`Rate limit hit. Retrying in ${retryAfter} seconds...`);

            await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));

            // Retry logic with decreasing retry count
            if (retries > 0) {
                return generateTaskList(title, description, retries - 1);
            }
        }
        console.error('Error generating task list:', error);
        throw error;
    }
};

export const generateUsabilitySummary = async (url, type = 'brief', retries = 3) => {
    console.log('Generating usability summary for url:', url);
    try {
        if(!url || url.length === 0) return null;
        if(!type || type.length === 0) type = 'brief';
        if(retries > 3) retries = 3;

        const response = await supportAPI.post(`/openai/generate-usability-summary`, {
            url: url,
            type: type
        });

        return response.data.summary.trim();


    } catch(error) {
        if (error.response && error.response.status === 429) {
            const retryAfter = error.response.headers['retry-after'] || 2; // Default wait of 2 seconds if not specified
            console.warn(`Rate limit hit. Retrying in ${retryAfter} seconds...`);

            await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));

            // Retry logic with decreasing retry count
            if (retries > 0) {
                return generateUsabilitySummary(url, type, retries - 1);
            }
        }
        console.error('Error generating usability summary',error.message);
    }
}

export const generateInvoiceSummary = async (items, retries=3) => {
    console.log('Generating invoice summary for items:', items);
    try {
        if(!items || items.length === 0) return null;

        const response = await supportAPI.post(`/openai/generate-invoice-summary`, {
            items: items
        });

        return response.data.summary.trim();


    } catch(error) {
        if (error.response && error.response.status === 429) {
            const retryAfter = error.response.headers['retry-after'] || 2; // Default wait of 2 seconds if not specified
            console.warn(`Rate limit hit. Retrying in ${retryAfter} seconds...`);

            await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));

            // Retry logic with decreasing retry count
            if (retries > 0) {
                return generateInvoiceSummary(items, retries - 1);
            }
        }
        console.error('Error generating invoice summary',error.message);
    }
}